import { Backdrop, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "./AppContext";
import OpeningCreateDialog from "./pages/POSEntry/OpeningCreateDialog";
import { useSearchPOSOpening } from "./shared/hooks/usePOSData";
import { useGetLoggedUser } from "./shared/hooks/useUser";

const AppInitializer = ({ children }) => {
    const queryClient = useQueryClient();
    const {
        globalState,
        setGlobalState,
        multipleCompanies,
        company,
        posProfile,
        settingsDetails,
        socket,
    } = useContext(AppContext);

    const isAutoCloseCash = useMemo(() => {
        if (settingsDetails) {
            return settingsDetails?.docs[0]?.auto_cash_close == 1;
        }
        return false;
    }, [settingsDetails?.docs[0]?.auto_cash_close]);

    const { data: userInfo } = useGetLoggedUser(true);
    const isKitchenUser = userInfo?.role_profile_name === "Kitchen";
    const isService = userInfo?.role_profile_name === "Service";
    const isHookah = userInfo?.role_profile_name === "Hookah";
    const isCallCenterUser = userInfo?.role_profile_name === "Call Center";

    const {
        data: todayOpen,
        refetch: refetchTodayOpen,
        isLoading: isLoadingTodayOpen,
    } = useSearchPOSOpening({
        user: userInfo?.name,
        company: company?.name || posProfile?.company,
        posProfile: posProfile?.name,
    });

    const [openingBalanceDialogOpen, setOpeningBalanceDialogOpen] =
        useState(false);

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!todayOpen) return;

        setLoading(false);

        const hasOpenEntryForCurrentContext = (todayOpen || []).some((opening) => {
            // Company remains the primary filter boundary.
            if (company?.name && opening?.company !== company.name) return false;
            // Opening entry must match selected POS Profile when profile exists.
            if (posProfile?.name && opening?.pos_profile !== posProfile.name)
                return false;
            return true;
        });

        setOpeningBalanceDialogOpen(!hasOpenEntryForCurrentContext);
    }, [todayOpen, company?.name, posProfile?.name]);

    useEffect(() => {
        if (
            !userInfo?.name ||
            isKitchenUser ||
            isService ||
            isHookah ||
            isCallCenterUser ||
            isAutoCloseCash
        ) {
            return;
        }

        let fallbackInterval = null;

        const stopFallbackPolling = () => {
            if (fallbackInterval) {
                window.clearInterval(fallbackInterval);
                fallbackInterval = null;
            }
        };
        const startFallbackPolling = () => {
            if (fallbackInterval) return;
            refetchTodayOpen();
            fallbackInterval = window.setInterval(refetchTodayOpen, 5000);
        };

        if (!socket?.on) {
            startFallbackPolling();
            return stopFallbackPolling;
        }

        const openingEntryDoctype = "POS Opening Entry";
        const subscribeToOpeningEntries = () => {
            stopFallbackPolling();
            socket.emit("doctype_subscribe", openingEntryDoctype);
            // Reconcile events that may have completed while disconnected.
            refetchTodayOpen();
        };
        const handleOpeningEntryUpdate = (data) => {
            if (data?.doctype === openingEntryDoctype) {
                queryClient.invalidateQueries(["searchPOSOpening"]);
            }
        };

        if (socket.connected) {
            subscribeToOpeningEntries();
        } else {
            startFallbackPolling();
        }
        socket.on("connect", subscribeToOpeningEntries);
        socket.on("disconnect", startFallbackPolling);
        socket.on("connect_error", startFallbackPolling);
        socket.on("list_update", handleOpeningEntryUpdate);

        return () => {
            stopFallbackPolling();
            if (socket.off) {
                socket.off("connect", subscribeToOpeningEntries);
                socket.off("disconnect", startFallbackPolling);
                socket.off("connect_error", startFallbackPolling);
                socket.off("list_update", handleOpeningEntryUpdate);
            } else if (socket.removeListener) {
                socket.removeListener("connect", subscribeToOpeningEntries);
                socket.removeListener("disconnect", startFallbackPolling);
                socket.removeListener("connect_error", startFallbackPolling);
                socket.removeListener("list_update", handleOpeningEntryUpdate);
            }
            socket.emit("doctype_unsubscribe", openingEntryDoctype);
        };
    }, [
        socket,
        queryClient,
        refetchTodayOpen,
        userInfo?.name,
        isKitchenUser,
        isService,
        isHookah,
        isCallCenterUser,
        isAutoCloseCash,
    ]);

    const getLoggedInUser = () => {
        if (userInfo) return userInfo.name;
        else return "";
    };

    useEffect(() => {
        if (!globalState.loggedInUser) {
            setGlobalState({
                ...globalState,
                loggedInUser: getLoggedInUser(),
            });
        }
    }, []);

    return (
        <>
            {isKitchenUser ||
            isService ||
            isHookah ||
            isCallCenterUser ||
            isAutoCloseCash ? (
                <>{children}</>
            ) : loading || !userInfo || isLoadingTodayOpen || !posProfile ? (
                <CircularProgress />
            ) : (
                <>
                    {children}

                    <Backdrop
                        sx={{
                            color: "#fff",
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                            position: "absolute",
                            backgroundColor: "rgba(33, 43, 54, 0.75)",
                        }}
                        open={openingBalanceDialogOpen}
                    >
                        <OpeningCreateDialog
                            dialogOpen={openingBalanceDialogOpen}
                            onConfirm={() => {
                                setOpeningBalanceDialogOpen(false);
                            }}
                        ></OpeningCreateDialog>
                    </Backdrop>
                </>
            )}
        </>
    );
};

export default AppInitializer;
